import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "convex/react";
import { useUser, useOrganization } from "@clerk/clerk-react";
import { api } from "../../../../../convex/_generated/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { EditProfileSheet } from "./EditProfileSheet";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { formatPhoneNumber } from "@/lib/phoneUtils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import {
  Building2,
  MapPin,
  Globe,
  Mail,
  Phone,
  Edit,
  Upload,
  Plus,
  Trash2,
  Sparkles,
  Layers,
  Loader2,
  User,
  DoorOpen,
  Palette,
} from "lucide-react";

interface CompanyProfileProps {
  initialData?: {
    name: string;
    contactName?: string;
    jobTitle?: string;
    logo: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    email: string;
    website: string;
    description: string;
    yearFounded: string;
    employeeCount: string;
    certifications: string[];
    cleaningServices?: string[];
    floorcareServices?: string[];
    serviceAreas?: Array<{ city: string; state: string }>;
  };
}

// Default fallback data for new organizations
const getDefaultCompanyData = (orgName?: string) => ({
  name: orgName || "Your Company",
  contactName: "",
  jobTitle: "",
  contactEmail: "",
  contactPhone: "",
  logo: "", // No default logo URL - will show Building2 icon fallback
  address: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  email: "",
  website: "",
  description: "",
  yearFounded: "",
  employeeCount: "",
  certifications: [] as string[],
  cleaningServices: [] as string[],
  floorcareServices: [] as string[],
  industry: "",
  serviceAreas: [] as Array<{ city: string; state: string }>,
});

export default function CompanyProfile(_props: CompanyProfileProps) {
  // Fetch user and organization data
  const { user } = useUser();
  const { organization: clerkOrganization, membership } = useOrganization();
  const organization = useQuery(api.organizations.getByUser, user ? {} : "skip");

  // Only owners and admins can see verification features
  const isOwnerOrAdmin =
    !clerkOrganization || // personal workspace = owner
    membership?.role === "org:admin";
  const updateProfile = useMutation(api.organizations.updateProfile);

  // Profile status for completeness

  // verificationStatus removed – organizationVerification module was deleted

  // Fetch profile summary for contact information (same as application preview)
  const profileSummary = useQuery(
    api.organizationProfile.getApplicationProfileSummary,
    organization ? {} : "skip"
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newCleaningService, setNewCleaningService] = useState("");
  const [newFloorcareService, setNewFloorcareService] = useState("");
  const [customCleaningService, setCustomCleaningService] = useState("");
  const [customFloorcareService, setCustomFloorcareService] = useState("");

  // Create company data from organization data with fallbacks
  // Use Clerk organization name, fallback to Convex organization name
  const organizationName = clerkOrganization?.name || organization?.name || "";

  const companyData = organization
    ? {
        name: organizationName,
        contactName: organization.contactName || "",
        jobTitle: organization.jobTitle || "",
        contactEmail: organization.contactEmail || "",
        contactPhone: organization.contactPhone || "",
        logo: organization.logo || "", // No default logo URL - will show Building2 icon fallback
        address: organization.address || "",
        city: organization.city || "",
        state: organization.state || "",
        zip: organization.zip || "",
        phone: organization.phone || "",
        email: organization.email || "", // Company email, not user email
        website: organization.website || "",
        description: organization.description || "",
        yearFounded: organization.yearFounded || "",
        employeeCount: organization.employeeCount || "",
        certifications: organization.certifications || [],
        cleaningServices: organization.cleaningServices || [],
        floorcareServices: organization.floorcareServices || [],
        industry: organization.industry || "",
        serviceAreas: organization.serviceAreas || [],
      }
    : getDefaultCompanyData(organizationName);

  const [localCompanyData, setLocalCompanyData] = useState(companyData);

  // Update local data when organization data changes
  useEffect(() => {
    if (organization) {
      const organizationName = clerkOrganization?.name || organization?.name || "";
      const newData = {
        name: organizationName,
        contactName: organization.contactName || "",
        jobTitle: organization.jobTitle || "",
        contactEmail: organization.contactEmail || "",
        contactPhone: organization.contactPhone || "",
        logo: organization.logo || "", // No default logo URL - will show Building2 icon fallback
        address: organization.address || "",
        city: organization.city || "",
        state: organization.state || "",
        zip: organization.zip || "",
        phone: organization.phone || "",
        email: organization.email || "", // Company email only
        website: organization.website || "",
        description: organization.description || "",
        yearFounded: organization.yearFounded || "",
        employeeCount: organization.employeeCount || "",
        certifications: organization.certifications || [],
        cleaningServices: organization.cleaningServices || [],
        floorcareServices: organization.floorcareServices || [],
        industry: organization.industry || "",
        serviceAreas: organization.serviceAreas || [],
      };
      setLocalCompanyData(newData);
    }
  }, [organization, user, clerkOrganization]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setLocalCompanyData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!organization) return;

    setIsSaving(true);
    try {
      await updateProfile({
        // Note: organization name is not saved (comes from Clerk)
        contactName: localCompanyData.contactName,
        jobTitle: localCompanyData.jobTitle,
        contactEmail: localCompanyData.contactEmail,
        contactPhone: localCompanyData.contactPhone,
        logo: localCompanyData.logo,
        address: localCompanyData.address,
        city: localCompanyData.city,
        state: localCompanyData.state,
        zip: localCompanyData.zip,
        phone: localCompanyData.phone,
        email: localCompanyData.email,
        website: localCompanyData.website,
        description: localCompanyData.description,
        yearFounded: localCompanyData.yearFounded,
        employeeCount: localCompanyData.employeeCount,
        certifications: localCompanyData.certifications,
        cleaningServices: localCompanyData.cleaningServices,
        floorcareServices: localCompanyData.floorcareServices,
        industry: localCompanyData.industry,
        serviceAreas: localCompanyData.serviceAreas,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save profile:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSheet = async (updatedData: any) => {
    if (!organization) return;

    try {
      await updateProfile(updatedData);
      setIsEditSheetOpen(false);
      // Update local data to reflect changes
      setLocalCompanyData((prev) => ({
        ...prev,
        ...updatedData,
      }));
    } catch (error) {
      console.error("Failed to save profile:", error);
      throw error; // Re-throw to let the EditProfileSheet handle the error
    }
  };

  const handleAddCleaningService = () => {
    if (newCleaningService === "custom") {
      if (customCleaningService.trim() !== "") {
        setLocalCompanyData((prev) => ({
          ...prev,
          cleaningServices: [...(prev.cleaningServices || []), customCleaningService],
        }));
        setCustomCleaningService("");
      }
    } else if (newCleaningService) {
      setLocalCompanyData((prev) => ({
        ...prev,
        cleaningServices: [...(prev.cleaningServices || []), newCleaningService],
      }));
    }
    setNewCleaningService("");
  };

  const handleAddFloorcareService = () => {
    if (newFloorcareService === "custom") {
      if (customFloorcareService.trim() !== "") {
        setLocalCompanyData((prev) => ({
          ...prev,
          floorcareServices: [...(prev.floorcareServices || []), customFloorcareService],
        }));
        setCustomFloorcareService("");
      }
    } else if (newFloorcareService) {
      setLocalCompanyData((prev) => ({
        ...prev,
        floorcareServices: [...(prev.floorcareServices || []), newFloorcareService],
      }));
    }
    setNewFloorcareService("");
  };

  const handleRemoveCleaningService = (service: string) => {
    setLocalCompanyData((prev) => ({
      ...prev,
      cleaningServices: (prev.cleaningServices || []).filter((s: any) => s !== service),
    }));
  };

  const handleRemoveFloorcareService = (service: string) => {
    setLocalCompanyData((prev) => ({
      ...prev,
      floorcareServices: (prev.floorcareServices || []).filter((s: any) => s !== service),
    }));
  };

  // Show loading state while fetching organization data
  if (organization === undefined) {
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Company Profile</h1>
            <p className="mt-1 text-gray-500">Manage your company information</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          <span className="ml-2 text-gray-500">Loading profile...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Company Profile</h1>
          <p className="mt-1 text-gray-500">Manage your company information</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => (window.location.href = "/settings?tab=branding")}
            variant="outline"
            className="gap-2"
          >
            <Palette className="h-4 w-4" /> Set Company Branding
          </Button>
          <Button onClick={() => setIsEditSheetOpen(true)} variant="default" className="gap-2">
            <Edit className="h-4 w-4" /> Edit Profile
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column - Company Logo and Basic Info */}
        <Card className="space-y-6 p-6">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4">
              {localCompanyData.logo ? (
                <img
                  src={localCompanyData.logo}
                  alt={localCompanyData.name}
                  className="max-h-32 max-w-32 rounded-lg border border-gray-200 object-contain"
                />
              ) : (
                <div
                  className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-100 transition-colors hover:bg-gray-50"
                  onClick={() => (window.location.href = "/settings?tab=branding")}
                >
                  <Building2 className="mb-2 h-10 w-10 text-gray-400" />
                  <span className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                    <Upload className="h-3 w-3" /> Upload Logo
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-col items-center space-y-2"></div>
            {isEditing && (
              <Button variant="outline" size="sm" className="mt-2 gap-2">
                <Upload className="h-4 w-4" /> Upload Logo
              </Button>
            )}
          </div>

          <div className="space-y-4">
            {!isEditing && (
              <>
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
                    Company
                  </h3>
                  <div className="flex items-center gap-2 text-gray-600">
                    <DoorOpen className="h-4 w-4" />
                    <span className="font-medium">{localCompanyData.name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {localCompanyData.address ||
                    localCompanyData.city ||
                    localCompanyData.state ||
                    localCompanyData.zip
                      ? [
                          localCompanyData.address,
                          localCompanyData.city,
                          localCompanyData.state,
                          localCompanyData.zip,
                        ]
                          .filter(Boolean)
                          .join(", ")
                      : "Address not specified"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span>
                    {localCompanyData.phone
                      ? formatPhoneNumber(localCompanyData.phone)
                      : "Phone number not specified"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span>{localCompanyData.email || "Email not specified"}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Globe className="h-4 w-4" />
                  <span>{localCompanyData.website || "Website not specified"}</span>
                </div>
              </>
            )}
          </div>

          {/* Contact Information Section - Under Company Information */}
          {!isEditing && profileSummary && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
                Company Representative
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="h-4 w-4" />
                  <span>{profileSummary.contactName}</span>
                  {profileSummary.jobTitle && (
                    <span className="text-gray-500">• {profileSummary.jobTitle}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span>{profileSummary.contactEmail || "No email provided"}</span>
                </div>
                {profileSummary.contactPhone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="h-4 w-4" />
                    <span>{formatPhoneNumber(profileSummary.contactPhone)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {isEditing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="employeeCount">Number of Employees</Label>
                <Input
                  id="employeeCount"
                  name="employeeCount"
                  value={localCompanyData.employeeCount}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}
        </Card>

        {/* Right Column - Tabbed Panel */}
        <Card className="p-6 lg:col-span-2">
          {isEditing ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={localCompanyData.address}
                    onChange={handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={localCompanyData.city}
                    onChange={handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="state"
                    value={localCompanyData.state}
                    onChange={handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip">ZIP Code</Label>
                  <Input id="zip" name="zip" value={localCompanyData.zip} onChange={handleChange} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={localCompanyData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    value={localCompanyData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  name="website"
                  value={localCompanyData.website}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Company Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={localCompanyData.description}
                  onChange={handleChange}
                  rows={5}
                />
              </div>

              <Separator className="my-4" />

              <div className="space-y-6">
                <h2 className="text-lg font-semibold">Services Offered</h2>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-md mb-2 flex items-center gap-2 font-medium">
                      <Sparkles className="h-4 w-4 text-blue-500" /> Cleaning Services
                    </h3>

                    <div className="mb-3 flex flex-wrap gap-2">
                      {(localCompanyData.cleaningServices || []).map(
                        (service: string, index: number) => (
                          <Badge
                            key={index}
                            variant="secondary"
                            className="flex items-center gap-1 px-3 py-1 text-sm"
                          >
                            {service}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="ml-1 h-4 w-4 p-0"
                              onClick={() => handleRemoveCleaningService(service)}
                            >
                              <Trash2 className="h-3 w-3 text-gray-500" />
                            </Button>
                          </Badge>
                        )
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Select value={newCleaningService} onValueChange={setNewCleaningService}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a cleaning service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="General Cleaning">General Cleaning</SelectItem>
                          <SelectItem value="Post Construction Cleaning">
                            Post Construction Cleaning
                          </SelectItem>
                          <SelectItem value="Disinfectant Cleaning">
                            Disinfectant Cleaning
                          </SelectItem>
                          <SelectItem value="Restroom Cleaning">Restroom Cleaning</SelectItem>
                          <SelectItem value="Exterior Cleaning">Exterior Cleaning</SelectItem>
                          <SelectItem value="Pressure Washing">Pressure Washing</SelectItem>
                          <SelectItem value="Tile & Grout Cleaning">
                            Tile & Grout Cleaning
                          </SelectItem>
                          <SelectItem value="Window & Glass Cleaning">
                            Window & Glass Cleaning
                          </SelectItem>
                          <SelectItem value="Trash Chute & Dumpster Area Cleaning">
                            Trash Chute & Dumpster Area Cleaning
                          </SelectItem>
                          <SelectItem value="Commercial Kitchen Cleaning">
                            Commercial Kitchen Cleaning
                          </SelectItem>
                          <SelectItem value="Graffiti Removal">Graffiti Removal</SelectItem>
                          <SelectItem value="Odor Removal">Odor Removal</SelectItem>
                          <SelectItem value="Upholstery & Fabric Cleaning">
                            Upholstery & Fabric Cleaning
                          </SelectItem>
                          <SelectItem value="Warehouse & Storage Area Cleaning">
                            Warehouse & Storage Area Cleaning
                          </SelectItem>
                          <SelectItem value="High-Touch Surface Sanitization">
                            High-Touch Surface Sanitization
                          </SelectItem>
                          <SelectItem value="Equipment & Machinery Cleaning">
                            Equipment & Machinery Cleaning
                          </SelectItem>
                          <SelectItem value="Specialty Cleaning">Specialty Cleaning</SelectItem>
                          <SelectItem value="Move-In/Move-Out Cleaning">
                            Move-In/Move-Out Cleaning
                          </SelectItem>
                          <SelectItem value="custom">Add Custom Service</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button onClick={handleAddCleaningService} disabled={!newCleaningService}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {newCleaningService === "custom" && (
                      <div className="mt-2">
                        <Input
                          placeholder="Enter custom cleaning service"
                          value={customCleaningService}
                          onChange={(e) => setCustomCleaningService(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-md mb-2 flex items-center gap-2 font-medium">
                      <Layers className="h-4 w-4 text-blue-500" /> Floorcare Services
                    </h3>

                    <div className="mb-3 flex flex-wrap gap-2">
                      {(localCompanyData.floorcareServices || []).map(
                        (service: any, index: any) => (
                          <Badge
                            key={index}
                            variant="secondary"
                            className="flex items-center gap-1 px-3 py-1 text-sm"
                          >
                            {service}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="ml-1 h-4 w-4 p-0"
                              onClick={() => handleRemoveFloorcareService(service)}
                            >
                              <Trash2 className="h-3 w-3 text-gray-500" />
                            </Button>
                          </Badge>
                        )
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Select value={newFloorcareService} onValueChange={setNewFloorcareService}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a floorcare service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Carpet Cleaning">Carpet Cleaning</SelectItem>
                          <SelectItem value="Carpet Extraction">Carpet Extraction</SelectItem>
                          <SelectItem value="Floor Stripping and Waxing">
                            Floor Stripping and Waxing
                          </SelectItem>
                          <SelectItem value="Floor Top Scrubbing and Waxing">
                            Floor Top Scrubbing and Waxing
                          </SelectItem>
                          <SelectItem value="Floor Stripping">Floor Stripping</SelectItem>
                          <SelectItem value="Floor Waxing">Floor Waxing</SelectItem>
                          <SelectItem value="Floor Buffing">Floor Buffing</SelectItem>
                          <SelectItem value="Tile & Grout Cleaning">
                            Tile & Grout Cleaning
                          </SelectItem>
                          <SelectItem value="Concrete Polishing">Concrete Polishing</SelectItem>
                          <SelectItem value="Hardwood Floor Refinishing">
                            Hardwood Floor Refinishing
                          </SelectItem>
                          <SelectItem value="Stone Floor Restoration">
                            Stone Floor Restoration
                          </SelectItem>
                          <SelectItem value="Vinyl Floor Maintenance">
                            Vinyl Floor Maintenance
                          </SelectItem>
                          <SelectItem value="Marble Floor Polishing">
                            Marble Floor Polishing
                          </SelectItem>
                          <SelectItem value="Terrazzo Floor Restoration">
                            Terrazzo Floor Restoration
                          </SelectItem>
                          <SelectItem value="custom">Add Custom Service</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button onClick={handleAddFloorcareService} disabled={!newFloorcareService}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {newFloorcareService === "custom" && (
                      <div className="mt-2">
                        <Input
                          placeholder="Enter custom floorcare service"
                          value={customFloorcareService}
                          onChange={(e) => setCustomFloorcareService(e.target.value)}
                          className="w-full"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Button onClick={handleSave} className="mt-6 w-full" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          ) : (
            <div className="mt-4 space-y-6">
              <div>
                <h2 className="mb-2 text-lg font-semibold">Company Description</h2>
                <p className="text-gray-600">
                  {localCompanyData.description || "No description provided yet."}
                </p>
              </div>

              <div>
                <h2 className="mb-2 text-lg font-semibold">Company Details</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Year Founded</h3>
                    <p className="text-900 text-gray-900">
                      {localCompanyData.yearFounded || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Number of Employees</h3>
                    <p className="text-900 text-gray-900">
                      {localCompanyData.employeeCount || "Not specified"}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="mb-2 text-lg font-semibold">Certifications</h2>
                <div className="flex flex-wrap gap-2">
                  {localCompanyData.certifications.length > 0 ? (
                    localCompanyData.certifications.map((cert: any, index: any) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800"
                      >
                        {cert}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No certifications added yet.</p>
                  )}
                </div>
              </div>

              <Separator className="my-4" />

              <div>
                <h2 className="mb-4 text-lg font-semibold">Services Offered</h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-md mb-2 flex items-center gap-2 font-medium">
                      <Sparkles className="h-4 w-4 text-blue-500" /> Cleaning Services
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {(localCompanyData.cleaningServices || []).length > 0 ? (
                        localCompanyData.cleaningServices?.map((service: any, index: any) => (
                          <Badge key={index} variant="secondary" className="px-3 py-1 text-sm">
                            {service}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No cleaning services added yet.</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-md mb-2 flex items-center gap-2 font-medium">
                      <Layers className="h-4 w-4 text-blue-500" /> Floorcare Services
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {(localCompanyData.floorcareServices || []).length > 0 ? (
                        localCompanyData.floorcareServices?.map((service: any, index: any) => (
                          <Badge key={index} variant="secondary" className="px-3 py-1 text-sm">
                            {service}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No floorcare services added yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <Separator className="my-4" />

              <div>
                <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold">
                  <MapPin className="h-4 w-4 text-blue-500" /> Service Areas
                </h2>
                <div className="flex flex-wrap gap-2">
                  {localCompanyData.serviceAreas && localCompanyData.serviceAreas.length > 0 ? (
                    localCompanyData.serviceAreas.map((area: any, index: any) => (
                      <Badge key={index} variant="outline" className="px-3 py-1 text-sm">
                        {area.city}, {area.state}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No service areas added yet.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
      <EditProfileSheet
        isOpen={isEditSheetOpen}
        onClose={() => setIsEditSheetOpen(false)}
        companyData={localCompanyData}
        onSave={handleSaveSheet}
      />
    </div>
  );
}
